import React from "react";
import { Select } from "@medentic/polaris-react";
import useLanguages from "@shared/hooks/common/useLanguages";
import { useTranslation } from "react-i18next";

interface LanguagePickerProps {
  languageCode: string | null;
  onChange: (languageCode: string | null) => void;
  filteredLanguageCodeList?: string[] | null;
  label?: string;
  helpText?: string;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  error?: string | boolean;
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  languageCode,
  onChange,
  filteredLanguageCodeList = null,
  label,
  helpText,
  error,
  disabled = false,
  placeholder,
  required = false,
}) => {
  const { t } = useTranslation();
  const { languages, isLoading } = useLanguages();

  // Normalize all language codes to lowercase for consistent matching
  if (filteredLanguageCodeList && filteredLanguageCodeList.length > 0) {
    filteredLanguageCodeList = filteredLanguageCodeList.map((code) => code.toLowerCase());
  } else {
    filteredLanguageCodeList = null;
  }

  // Early return with loading state if data isn't ready
  if (isLoading || !languages) {
    return (
      <Select
        label={label}
        options={[
          {
            label: t("common.loading"),
            value: "__loading__",
            disabled: true,
          },
        ]}
        value="__loading__"
        onChange={() => {}}
        disabled={true}
        requiredIndicator={required}
      />
    );
  }

  // Filter languages if filteredLanguageCodeList is provided
  const availableLanguages = filteredLanguageCodeList && languages.length > 0
    ? languages.filter((language) => {
        if (!language?.iso) return false;
        
        return filteredLanguageCodeList.some((code) => {
          if (!code) return false;
          
          const codeStr = String(code).toLowerCase();
          const languageIso = String(language.iso).toLowerCase();
          
          return codeStr === languageIso;
        });
      })
    : languages || [];

  // Create options array with placeholder option
  interface ListItem {
    label: string;
    value: string;
    disabled?: boolean;
  }
  
  const languageOptions: ListItem[] = availableLanguages
  .filter((language) => 
    language && 
    language.localLabel &&
    language.iso
  )
  .map((language): ListItem => ({
    label: language.localLabel,
    value: language.iso,
  }));

// Only add placeholder option if no language is selected
const options: ListItem[] = languageCode 
  ? languageOptions
  : [
      {
        label: placeholder || t("common.selectOption"),
        value: "__placeholder__",
        disabled: true,
      },
      ...languageOptions,
    ];

  const handleChange = (selectedValue: string) => {
    // If placeholder is selected, return null
    if (selectedValue === "__placeholder__") {
      onChange(null);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <Select
      label={label}
      options={options}
      value={languageCode || "__placeholder__"}
      onChange={handleChange}
      error={error}
      helpText={helpText}
      disabled={disabled}
      requiredIndicator={required}
    />
  );
};

export default LanguagePicker;
